import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  Grid, 
  CircularProgress, 
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Fade,
  Pagination,
  Paper,
  Divider,
  Button,
  Link,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CategoryIcon from '@mui/icons-material/Category';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SecurityIcon from '@mui/icons-material/Security';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Star from '@mui/icons-material/Star';
import { api } from '../services/api';
import { Agent } from '../types/Agent';
import AgentCard from '../components/AgentCard';
import { styled } from '@mui/material/styles';

// Styles personnalisés avec palette noir, blanc et gris foncé
const SearchBarWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: '#ffffff',
  boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.06)',
  margin: '40px 0',
  width: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.1)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 2,
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
  },
  '& .MuiInputBase-input': {
    padding: '18px 14px',
  }
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius * 2,
  fontWeight: 500,
  backgroundColor: '#f5f5f5',
  color: '#333333',
  '&.MuiChip-filled': {
    backgroundColor: '#333333',
    color: '#ffffff',
  },
  '&.MuiChip-outlined': {
    borderColor: '#333333',
    color: '#333333',
  }
}));

const PageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #333333 0%, #1a1a1a 100%)`,
  padding: theme.spacing(6, 0),
  borderRadius: theme.shape.borderRadius * 2,
  marginBottom: theme.spacing(4),
  color: '#ffffff',
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
  }
}));

const FaqItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: '1px solid #e0e0e0',
  '&:last-child': {
    borderBottom: 'none',
  }
}));

const ContentSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(6),
  padding: theme.spacing(4),
  backgroundColor: '#ffffff',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
}));

const AgentsPage = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const agentsPerPage = 9;
  const [totalPages, setTotalPages] = useState(1);

  // Simuler des catégories (à remplacer avec les vraies données si possible, ex: fetch depuis API)
  const categories = ['Tous', 'Service Client', 'Génération d\'image', 'Marketing', 'Productivité', 'Analyse', 'Développement', 'Web', 'Outils', '3D']; // Ajout des catégories de l'agent HF

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        console.log('Tentative de récupération des agents...');
        const agentsData = await api.getAllAgents(); // Charger TOUS les agents depuis l'API
        console.log('Agents API récupérés (incluant potentiellement HF):', agentsData);

        setAgents(agentsData);
        setFilteredAgents(agentsData); // Initialiser avec tous les agents
        setTotalPages(Math.ceil(agentsData.length / agentsPerPage));
      } catch (err) {
        console.error('Erreur lors de la récupération des agents:', err);
        setError('Erreur lors du chargement des agents');
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      filterAgents('', selectedCategory);
    } else {
      const timer = setTimeout(() => {
        filterAgents(query, selectedCategory);
      }, 300);
      return () => clearTimeout(timer);
    }
  };

  // Fonction pour vérifier si un agent appartient à une catégorie (déplacée ici pour clarté)
  const isAgentInCategory = (agent: Agent, category: string): boolean => {
    // Utilise le champ 'category' qui est maintenant dans le type Agent
    return agent.category?.includes(category) ?? false; // Vérifie si le tableau category inclut la catégorie
  };

  const filterAgents = async (query: string, category: string | null) => {
    try {
      setLoading(true);
      let resultsFromApi;

      if (query.trim() === '') {
        // Si pas de recherche, on utilise les agents déjà chargés pour éviter un fetch inutile
        // ou on refetch si on veut toujours les données les plus fraîches
        // Option 1: Utiliser les agents en mémoire
        resultsFromApi = agents; 
        // Option 2: Refetch (comme avant)
        // resultsFromApi = await api.getAllAgents();
      } else {
        // Si recherche, appeler l'API de recherche
        resultsFromApi = await api.searchAgents(query);
        // NOTE: L'API searchAgents retournera-t-elle l'agent HF si on cherche "DeepSite" ?
        // Si non, la recherche le fera disparaître. Il faudrait potentiellement ajuster l'API search
        // ou ajouter manuellement l'agent HF aux résultats de recherche s'il correspond au query.
        // Pour l'instant, on suppose que l'API search peut le trouver.
      }

      // Filtrer par catégorie si une catégorie est sélectionnée
      let finalFilteredResults = resultsFromApi;
      if (category && category !== 'Tous') {
         // Filtrer les résultats (qui viennent maintenant de l'état local ou de la recherche API)
        finalFilteredResults = resultsFromApi.filter((agent: Agent) => isAgentInCategory(agent, category));
      }

      setFilteredAgents(finalFilteredResults);
      setCurrentPage(1);
      setTotalPages(Math.ceil(finalFilteredResults.length / agentsPerPage));

    } catch (err) {
      setError('Une erreur est survenue lors de la recherche/filtrage.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category: string) => {
    const categoryValue = category === 'Tous' ? null : category;
    setSelectedCategory(categoryValue);
    filterAgents(searchQuery, categoryValue);
  };

  // Pagination
  const indexOfLastAgent = currentPage * agentsPerPage;
  const indexOfFirstAgent = indexOfLastAgent - agentsPerPage;
  const currentAgents = filteredAgents.slice(indexOfFirstAgent, indexOfLastAgent);
  const pageCount = Math.ceil(filteredAgents.length / agentsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && agents.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '70vh',
      }}>
        <CircularProgress size={60} sx={{ color: '#333333' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh', 
      pb: 8,
      pt: '100px',
    }}>
      <Container maxWidth="lg">
        <PageHeader>
          <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ fontWeight: 700 }}>
            Explorez Nos Agents IA
          </Typography>
          <Typography variant="h6" align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
            Découvrez notre catalogue d'agents IA : automatisez, créez, amusez-vous facilement depuis Famber.
          </Typography>
        </PageHeader>

        {/* Barre de recherche moderne */}
        <SearchBarWrapper>
          <StyledTextField
            fullWidth
            placeholder="Rechercher un agent par nom, compétence ou fonctionnalité..."
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#333333' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <FilterListIcon sx={{ color: '#666666' }} />
                </InputAdornment>
              ),
            }}
          />
        </SearchBarWrapper>

        {/* Filtres par catégorie */}
        <Box sx={{ display: 'flex', mb: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
            <CategoryIcon fontSize="small" sx={{ mr: 1, color: '#333333' }} />
            <Typography variant="body2" fontWeight={500} sx={{ color: '#333333' }}>
              Catégories :
            </Typography>
          </Box>
          {categories.map((category) => (
            <FilterChip
              key={category}
              label={category}
              onClick={() => handleCategoryChange(category)}
              variant={selectedCategory === category || (category === 'Tous' && !selectedCategory) ? 'filled' : 'outlined'}
              size="medium"
            />
          ))}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Affichage des résultats */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, color: '#333333' }}>
            {filteredAgents.length} agents trouvés
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {filteredAgents.map((agent) => (
            <Grid item xs={12} sm={6} md={4} key={agent._id}>
              <Box sx={{ height: '100%', display: 'flex' }}>
                <AgentCard agent={agent} />
              </Box>
            </Grid>
          ))}
        </Grid>

        {filteredAgents.length === 0 && !loading && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8, 
            backgroundColor: '#ffffff', 
            borderRadius: 2,
            mt: 4,
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#333333' }}>
              Aucun agent ne correspond à votre recherche
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              Essayez de modifier vos critères de recherche ou explorez d'autres catégories
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination 
              count={pageCount} 
              page={currentPage} 
              onChange={handlePageChange} 
              size="large"
              showFirstButton 
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#333333',
                },
                '& .Mui-selected': {
                  backgroundColor: '#333333 !important',
                  color: '#ffffff !important',
                },
              }}
            />
          </Box>
        )}

        {/* NOUVELLE SECTION: Avantages des agents IA */}
        <ContentSection>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#222222', textAlign: 'center' }}>
            Pourquoi choisir nos agents IA?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <InfoCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SmartToyIcon sx={{ fontSize: 32, color: '#333333', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Intelligence avancée
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#555555', mb: 2 }}>
                  Nos agents utilisent les technologies d'IA les plus récentes pour comprendre vos besoins et s'adapter à votre entreprise avec précision.
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ color: '#777777', mt: 2, fontStyle: 'italic' }}>
                  "L'intelligence artificielle est la nouvelle électricité des entreprises modernes."
                </Typography>
              </InfoCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessCenterIcon sx={{ fontSize: 32, color: '#333333', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Efficacité opérationnelle
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#555555', mb: 2 }}>
                Augmentez votre productivité et réduisez les coûts grâce à l'automatisation intelligente des tâches répétitives et la prise en charge des interactions client.
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ color: '#777777', mt: 2, fontStyle: 'italic' }}>
                  "Nos clients constatent une amélioration de l'efficacité de 45% en moyenne."
                </Typography>
              </InfoCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SupportAgentIcon sx={{ fontSize: 32, color: '#333333', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Disponibilité 24/7
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#555555', mb: 2 }}>
                  Nos agents IA travaillent sans interruption, offrant à vos clients une assistance immédiate à tout moment, partout dans le monde.
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ color: '#777777', mt: 2, fontStyle: 'italic' }}>
                  "L'excellence du service client ne connaît plus de contraintes horaires."
                </Typography>
              </InfoCard>
            </Grid>
          </Grid>
        </ContentSection>

        {/* SECTION: Comment ça marche */}
        <ContentSection>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#222222', textAlign: 'center' }}>
            Comment intégrer nos agents à votre entreprise
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    backgroundColor: '#333333', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 30, 
                    height: 30, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2 
                  }}>1</Box>
                  Sélectionnez un agent adapté à vos besoins
                </Typography>
                <Typography variant="body1" sx={{ ml: 6, mb: 3, color: '#555555' }}>
                  Explorez notre catalogue et identifiez les agents qui correspondent aux exigences spécifiques de votre entreprise.
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    backgroundColor: '#333333', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 30, 
                    height: 30, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2 
                  }}>2</Box>
                  Configuration simple et rapide
                </Typography>
                <Typography variant="body1" sx={{ ml: 6, mb: 3, color: '#555555' }}>
                  Nos experts vous accompagnent dans l'intégration de l'agent à vos systèmes existants avec un minimum d'effort technique.
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    backgroundColor: '#333333', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 30, 
                    height: 30, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2 
                  }}>3</Box>
                  Personnalisation et entraînement
                </Typography>
                <Typography variant="body1" sx={{ ml: 6, mb: 3, color: '#555555' }}>
                  Adaptez votre agent à votre marque et entraînez-le avec vos données pour des performances optimales.
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    backgroundColor: '#333333', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: 30, 
                    height: 30, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2 
                  }}>4</Box>
                  Déploiement et amélioration continue
                </Typography>
                <Typography variant="body1" sx={{ ml: 6, color: '#555555' }}>
                  Lancez votre agent et bénéficiez de mises à jour régulières basées sur les retours d'utilisation et l'évolution des technologies.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                p: 4, 
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.04)'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#333333' }}>
                  Prêt à transformer votre entreprise?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: '#555555' }}>
                  Nos consultants vous aident à sélectionner les agents adaptés à vos besoins spécifiques et à maximiser leur impact sur votre activité.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button 
                    variant="contained" 
                    size="large"
                    component="a"
                    href="https://tally.so/r/mJWEgJ"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      bgcolor: 'white', 
                      color: '#333333', 
                      borderRadius: '50px',
                      px: 4,
                      '&:hover': {
                        bgcolor: '#f0f0f0',
                      }
                    }}
                  >
                    Demander une démonstration
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large"
                    component="a"
                    href="https://calendly.com/fambercontact/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      borderColor: 'white', 
                      color: 'white', 
                      borderRadius: '50px',
                      px: 4,
                      '&:hover': {
                        borderColor: '#f0f0f0',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    Contacter un expert
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </ContentSection>

        {/* SECTION: FAQ */}
        {/* <ContentSection>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#222222', textAlign: 'center' }}>
            Questions fréquentes
          </Typography>
          <Box>
            <FaqItem>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ mr: 2, color: '#333333' }} />
                Comment les agents IA peuvent-ils améliorer le service client?
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, pl: 5, color: '#555555' }}>
                Nos agents IA offrent des réponses instantanées 24/7, réduisent les temps d'attente, personnalisent les interactions et permettent à vos employés de se concentrer sur des tâches à plus forte valeur ajoutée.
              </Typography>
            </FaqItem>
            <FaqItem>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ mr: 2, color: '#333333' }} />
                Quelle est la durée d'intégration d'un agent à mon système?
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, pl: 5, color: '#555555' }}>
                Le déploiement initial peut être réalisé en 2 à 4 semaines selon la complexité de votre infrastructure. Une personnalisation plus poussée peut nécessiter quelques semaines supplémentaires.
              </Typography>
            </FaqItem>
            <FaqItem>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ mr: 2, color: '#333333' }} />
                Les agents IA sont-ils sécurisés pour traiter des données sensibles?
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, pl: 5, color: '#555555' }}>
                Absolument. Nos agents respectent les normes les plus strictes en matière de sécurité et de confidentialité des données, avec chiffrement de bout en bout et conformité RGPD.
              </Typography>
            </FaqItem>
            <FaqItem>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ mr: 2, color: '#333333' }} />
                Puis-je personnaliser l'agent pour qu'il parle avec la voix de ma marque?
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, pl: 5, color: '#555555' }}>
                Oui, tous nos agents peuvent être entièrement personnalisés pour adopter le ton, le style et les valeurs de votre marque, créant ainsi une expérience cohérente pour vos clients.
              </Typography>
            </FaqItem>
          </Box>
        </ContentSection> */}

        {/* SECTION: Témoignages */}
        <Box sx={{ py: 12, backgroundColor: '#ffffff' }}>
          <Container maxWidth="lg">
            <Typography variant="h4" align="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold' }}>
              Ce que nos clients disent
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      component="img"
                      src="/jules.jpg"
                      alt="Jules"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        mr: 2,
                        objectFit: 'cover',
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Jules
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Business Manager, NeuronesIT
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                    "Famber a transformé notre approche de la prospection. Les agents IA nous permettent d'atteindre plus de prospects qualifiés en moins de temps."
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} sx={{ color: '#FFD700', fontSize: 24 }} />
                    ))}
                    <Typography variant="body2" sx={{ ml: 1, color: '#666666' }}>
                      5/5
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      component="img"
                      src="/arthur.jpg"
                      alt="Arthur"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        mr: 2,
                        objectFit: 'cover',
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Arthur
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Head of Brand, Conee's
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                    "L'automatisation intelligente de Famber nous a permis d'optimiser nos campagnes marketing et d'augmenter significativement notre ROI."
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {[1, 2, 3, 4].map((star) => (
                      <Star key={star} sx={{ color: '#FFD700', fontSize: 24 }} />
                    ))}
                    <Star sx={{ color: '#FFD700', fontSize: 24, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ ml: 1, color: '#666666' }}>
                      4.8/5
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Footer / Call-to-action */}
        <Box sx={{ 
          mt: 6, 
          p: 4, 
          borderRadius: 2, 
          bgcolor: '#333333', 
          color: 'white',
          textAlign: 'center'
        }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Prêt à révolutionner votre entreprise avec l'IA?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: 800, mx: 'auto', opacity: 0.9 }}>
            Explorez notre catalogue d'agents intelligents et découvrez comment ils peuvent transformer votre activité dès aujourd'hui.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button 
              variant="contained" 
              size="large"
              component="a"
              href="https://tally.so/r/mJWEgJ"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: 'white', 
                color: '#333333', 
                borderRadius: '50px',
                px: 4,
                '&:hover': {
                  bgcolor: '#f0f0f0',
                }
              }}
            >
              Demander une démonstration
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              component="a"
              href="https://calendly.com/fambercontact/30min"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                borderColor: 'white', 
                color: 'white', 
                borderRadius: '50px',
                px: 4,
                '&:hover': {
                  borderColor: '#f0f0f0',
                  bgcolor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              Contacter un expert
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default AgentsPage;